import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import { adminApi } from '../services/api';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import type { AdminHostAgent } from '../types/admin';

export default function SSHWindowPage() {
  const { id } = useParams();
  const { sshSocket: socket } = useSocket();

  const [hostAgent, setHostAgent] = useState<AdminHostAgent | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sshConnected, setSshConnected] = useState(false);

  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const connectAttemptedRef = useRef(false);
  const mountedRef = useRef(true);

  // Username may be used in future for page title; not needed in UI

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load agent details
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!id) throw new Error('Host Agent ID is missing');
        const data = await adminApi.getHostAgent(id);
        if (mounted) setHostAgent(data.hostAgent);
      } catch {
        if (mounted) setHostAgent(null);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Init xterm
  useEffect(() => {
    if (!terminalRef.current || xtermRef.current) return;
    const term = new XTerm({
      convertEol: false,
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
      fontSize: 16,
      scrollback: 2000,
      theme: { background: '#0a0a0f', foreground: '#e5e5e5', cursor: '#00e85e', cursorAccent: '#0a0a0f', selectionBackground: '#00e85e33' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(terminalRef.current);
    fit.fit();
    xtermRef.current = term;
    fitAddonRef.current = fit;
    let disposed = false;

    const onWindowResize = () => {
      if (disposed) return;
      try { fit.fit(); } catch { return; }
      if (socket && sessionIdRef.current && term.cols && term.rows) {
        socket.emit('ssh-resize', { sessionId: sessionIdRef.current, cols: term.cols, rows: term.rows });
      }
    };
    window.addEventListener('resize', onWindowResize);
    const ro = new ResizeObserver(() => onWindowResize());
    try { if (terminalRef.current) ro.observe(terminalRef.current); } catch {}
    const dispResize = term.onResize(({ cols, rows }) => {
      if (!disposed && socket && sessionIdRef.current) {
        socket.emit('ssh-resize', { sessionId: sessionIdRef.current, cols, rows });
      }
    });
    return () => {
      disposed = true;
      window.removeEventListener('resize', onWindowResize);
      try { ro.disconnect(); } catch {}
      dispResize.dispose();
      term.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, [socket]);

  // Bridge input
  useEffect(() => {
    const term = xtermRef.current;
    if (!term || !socket) return;
    const disp = term.onData((data: string) => {
      if (sessionIdRef.current) socket.emit('ssh-input', { sessionId: sessionIdRef.current, data });
    });
    return () => disp.dispose();
  }, [socket]);

  // Socket events
  useEffect(() => {
    if (!socket) return;

    const onSSHConnected = (data: { requestId?: string; sessionId: string; hostAgent: { name: string; ipAddress: string; username: string } }) => {
      if (data.requestId && data.requestId !== requestIdRef.current) return;
      sessionIdRef.current = data.sessionId;
      setSshConnected(true);
      setIsConnecting(false);
      const term = xtermRef.current;
      if (term) {
        term.writeln(`Connecting to ${data.hostAgent.ipAddress}...`);
        term.writeln(`Connected to ${data.hostAgent.name}`);
        term.writeln(`SSH connection established successfully`);
        setTimeout(() => {
          if (xtermRef.current !== term) return;
          fitAddonRef.current?.fit();
          term.focus();
          socket.emit('ssh-resize', { sessionId: data.sessionId, cols: term.cols, rows: term.rows });
        }, 0);
      }
    };

    const onSSHOutput = (data: { requestId?: string; stdout?: string; stderr?: string; stdoutChunk?: string; stderrChunk?: string }) => {
      if (data.requestId && data.requestId !== requestIdRef.current) return;
      const term = xtermRef.current;
      if (!term) return;
      if (data.stdout) term.write(data.stdout);
      if (data.stderr) term.write(data.stderr);
      if (data.stdoutChunk) term.write(data.stdoutChunk);
      if (data.stderrChunk) term.write(data.stderrChunk);
    };

    const onSSHError = (data: { requestId?: string; message: string }) => {
      if (data.requestId && data.requestId !== requestIdRef.current) return;
      setIsConnecting(false);
      xtermRef.current?.writeln(`\x1b[31mError:\x1b[0m ${data.message}`);
    };

    const onSSHDisconnected = (data?: { requestId?: string }) => {
      if (data?.requestId && data.requestId !== requestIdRef.current) return;
      sessionIdRef.current = null;
      requestIdRef.current = null;
      setSshConnected(false);
      xtermRef.current?.writeln('');
      xtermRef.current?.writeln('SSH connection closed.');
    };

    socket.on('ssh-connected', onSSHConnected);
    socket.on('ssh-output', onSSHOutput);
    socket.on('ssh-error', onSSHError);
    socket.on('ssh-disconnected', onSSHDisconnected);

    return () => {
      socket.off('ssh-connected', onSSHConnected);
      socket.off('ssh-output', onSSHOutput);
      socket.off('ssh-error', onSSHError);
      socket.off('ssh-disconnected', onSSHDisconnected);
    };
  }, [socket]);

  // Auto-connect when agent is loaded
  useEffect(() => {
    const go = async () => {
      if (!socket || !hostAgent || isConnecting || sshConnected || connectAttemptedRef.current) return;
      connectAttemptedRef.current = true;
      try {
        setIsConnecting(true);
        xtermRef.current?.clear();
        xtermRef.current?.writeln('Initiating SSH connection...');
        const resp = await adminApi.initializeSSH(hostAgent._id);
        if (!mountedRef.current) return;
        if (!resp?.success) {
          setIsConnecting(false);
          xtermRef.current?.writeln('SSH initialization failed');
          return;
        }
        const term = xtermRef.current;
        const requestId = globalThis.crypto.randomUUID();
        requestIdRef.current = requestId;
        socket.emit('ssh-connect', { requestId, hostAgentId: hostAgent._id, cols: term?.cols, rows: term?.rows });
      } catch (e) {
        setIsConnecting(false);
        xtermRef.current?.writeln(`\x1b[31mError:\x1b[0m ${(e as Error).message}`);
      }
    };
    go();
  }, [hostAgent, socket, isConnecting, sshConnected]);

  useEffect(() => {
    return () => {
      if (sessionIdRef.current && socket) {
        socket.emit('ssh-disconnect', { sessionId: sessionIdRef.current });
        sessionIdRef.current = null;
      } else if (requestIdRef.current && socket) {
        socket.emit('ssh-cancel', { requestId: requestIdRef.current });
      }
      requestIdRef.current = null;
    };
  }, [socket]);

  // No chrome/header in popup; close/disconnect are handled by window lifecycle

  return (
    <div className="w-screen h-screen bg-[#0a0a0f]">
      <div ref={terminalRef} className="w-full h-full" />
    </div>
  );
}
